import React from 'react';
import { motion } from 'framer-motion';

interface BreathtakingCardProps {
  title?: string;
  subtitle?: string;
  value?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon?: React.ReactNode;
  variant?: 'metric' | 'performance' | 'risk' | 'comparison';
  size?: 'sm' | 'md' | 'lg';
  children?: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

const BreathtakingCard: React.FC<BreathtakingCardProps> = ({
  title,
  subtitle,
  value,
  trend,
  icon,
  variant = 'metric',
  size = 'md',
  children,
  className = '',
  onClick
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'performance':
        return 'border-yellow-500/30 bg-gradient-to-br from-yellow-500/5 via-slate-800/90 to-slate-900/95';
      case 'risk':
        return 'border-red-500/30 bg-gradient-to-br from-red-500/5 via-slate-800/90 to-slate-900/95';
      case 'comparison':
        return 'border-blue-500/30 bg-gradient-to-br from-blue-500/5 via-slate-800/90 to-slate-900/95';
      default:
        return 'border-slate-500/30 bg-gradient-to-br from-slate-700/20 via-slate-800/90 to-slate-900/95';
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'p-4 min-h-32';
      case 'lg':
        return 'p-8 min-h-80';
      default:
        return 'p-6 min-h-48';
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'up':
        return 'text-green-400';
      case 'down':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={{ scale: 1.02, y: -2 }}
      transition={{ 
        duration: 0.3,
        type: "spring",
        stiffness: 300,
        damping: 20
      }}
      className={`
        relative overflow-hidden rounded-2xl border backdrop-blur-xl
        shadow-2xl hover:shadow-3xl transition-all duration-300
        cursor-pointer group
        ${getVariantStyles()}
        ${getSizeStyles()}
        ${className}
      `}
      onClick={onClick}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative z-10 h-full flex flex-col">
        {(title || icon) && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1 min-w-0">
              {title && (
                <h3 className="text-sm font-semibold text-slate-300 truncate tracking-wide uppercase">
                  {title}
                </h3>
              )}
            </div>
            {icon && (
              <div className="flex-shrink-0 ml-3">
                {icon}
              </div>
            )}
          </div>
        )}
        
        {value && (
          <div className="mb-2">
            <p className={`text-3xl font-bold truncate ${getTrendColor()}`}>
              {value}
            </p>
          </div>
        )}
        
        {subtitle && (
          <p className="text-sm text-slate-400 mb-4 truncate">
            {subtitle}
          </p>
        )}
        
        <div className="flex-1">
          {children}
        </div>
      </div>
    </motion.div>
  );
};

export default BreathtakingCard;
